import { PropertyValueMap, html, nothing } from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { ResizeObserver as ResizeObserverPolyfill } from '@juggle/resize-observer';
import { pdsCustomElement as customElement } from '../../decorators/pds-custom-element';
import { PdsFormElement } from '../pds-form-element/PdsFormElement';

import styles from './text-area.scss?inline';

// use polyfill if unsupported by browser
let ResizeObserver = ResizeObserverPolyfill;

/* istanbul ignore next */
if (typeof window !== 'undefined' && window.ResizeObserver) {
  /* istanbul ignore next */
  ResizeObserver = window.ResizeObserver;
}

/**
 * @summary The Principal text area component
 *
 * @fires pds-text-area-input an event dispatched on input
 * @fires pds-text-area-change an event dispatched on change
 * @fires pds-text-area-blur an event dispatched on blur
 * @fires pds-text-area-focus an event dispatched on focus
 * @slot label Optional: Use this slot instead of the label property, if the label requires additional markup.
 * @slot help-text Optional: Use this slot instead of the helpText property, if the help text requires additional markup.
 */
@customElement('pds-text-area', {
  category: 'component',
  type: 'component',
  state: 'stable',
  styles,
})
export class PdsTextArea extends PdsFormElement {
  connectedCallback() {
    super.connectedCallback();
    this.initLocalization();
  }

  /**
   * Style variant
   * - **default** renders the standard text-area color variant
   * - **inverted** renders the inverted text-area color variant
   */
  @property()
  variant: 'default' | 'inverted' = 'default';

  set value(newValue: string) {
    const oldValue = this.value;

    // store the value so it can be retrieved by the getter
    this.internalValue = newValue;

    // sets the current value of the control
    this.internals.setFormValue(newValue);

    // set the value on the field
    if (this.field && this.field.value !== newValue) {
      this.field.value = newValue;
    }
    // rerender the component
    this.requestUpdate('value', oldValue);
  }

  /**
   * The default text value.
   *
   * There is no HTML "value" attribute on the native `<textarea>` element.
   * Set the "value" on the `<pds-text-area>` web component to render default text content.
   * The default text is wrapped in the `<textarea>` tags, ie `<textarea>Default text content.</textarea>`.
   */
  @property()
  get value(): string {
    return this.internalValue;
  }

  /**
   * The size of the component.
   *
   * - **default**
   * - **sm** renders a the small version of the textarea
   *
   * NOTE: This is NOT the HTML size attribute that controls the width of the textarea.
   */
  @property()
  size: 'sm' | 'default' = 'default';

  /**
   * Standard textarea minlength attribute.
   * See [HTML attribute: minlenth](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/minlenth)
   */
  @property()
  minLength?: number;

  /**
   * Standard textarea maxlength attribute.
   * See [HTML attribute: maxlength](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/maxlength)
   */
  @property()
  maxLength?: number;

  /**
   * Resize controls.
   * Set to an allowed value of 'smart', 'manual', 'horizontal', 'vertical' or 'none'.
   * The default value is 'smart' which automatically resizes based on content height.
   * Works via CSS style rule that targets the attribute name and value.
   * See [Controlling whether a textarea is resizable](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/textarea#controlling_whether_a_textarea_is_resizable)
   * - **smart** causes automatic resizing of height based on content entry
   * - **manual** allows for resizing in all directions
   * - **horizontal** allows for resizing horizontally
   * - **vertical** allows for resizing vertically
   * - **none** disables resizing
   */
  @property()
  resize: 'smart' | 'manual' | 'horizontal' | 'vertical' | 'none' = 'smart';

  /**
   * Standard input autocomplete attribute.
   * See [HTML attribute: autocomplete](https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete)
   */
  @property()
  autocomplete: 'off' | 'on';

  /** @internal */
  @query('textarea')
  field: HTMLTextAreaElement;

  /**
   * @internal
   */
  @state()
  charactersRemaining?: number;

  /**
   * @internal
   */
  @state()
  resizeTimeout: NodeJS.Timeout | undefined;

  /**
   * Listen for resize events on an Element and update table component expandable rows
   *
   * @internal
   */
  @state()
  // We can't actually call the observer, because Jest has no concept of element width
  /* istanbul ignore next */
  resizeObserver = new ResizeObserver(() => {
    this.handleResizeCallback();
  });

  handleResizeCallback() {
    if (this.resize === 'smart') {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        this.setSmartHeight();
      }, 50);
    }
  }

  updateField() {
    // set the value on the field
    // when the field exists and there is an internalValue
    if (
      this.field &&
      typeof this.internalValue !== 'undefined' &&
      this.field.value !== this.internalValue
    ) {
      this.field.value = this.internalValue;
    }
  }

  getTextAreaDescribedBy() {
    if (this.maxLength !== undefined) {
      return this.getAriaDescribedBy().concat(
        ' pds-textarea-characters-remaining',
      );
    }

    return this.getAriaDescribedBy();
  }

  setSmartHeight() {
    if (this.field) {
      // Set height to auto before calculating new height using scrollHeight.
      this.field.style.height = 'auto';
      const borderHeight = 1;
      const paddingBoxHeight: number = this.field.scrollHeight;
      const borderBoxHeight: number = paddingBoxHeight + borderHeight * 2;
      this.field.style.height = `${borderBoxHeight}px`;
    }
  }

  private handleChange() {
    this.value = this.field.value;

    if (this.value === 'undefined') {
      this.value = '';
    }

    this.dispatchEvent(
      new Event('pds-text-area-change', { bubbles: true, composed: true }),
    );
    if (this.maxLength !== undefined) {
      this.charactersRemaining = this.maxLength - this.value.length;
    }
  }

  private handleInput() {
    this.value = this.field.value;
    this.dispatchEvent(
      new Event('pds-text-area-input', { bubbles: true, composed: true }),
    );
  }

  private handleBlur() {
    this.dispatchEvent(
      new Event('pds-text-area-blur', { bubbles: false, composed: true }),
    );
  }

  private handleFocus() {
    this.dispatchEvent(
      new Event('pds-text-area-focus', { bubbles: false, composed: true }),
    );
  }

  /** @internal */
  get classNames() {
    return {
      sm: this.size === 'sm',
      'is-error': !!this.errorMessage,
      'is-disabled': this.disabled,
      'hidden-label': this.hideLabel,
      [this.variant]: !!this.variant,
    };
  }

  protected override firstUpdated() {
    super.firstUpdated();

    this.updateField();

    if (this.resize === 'smart') {
      this.setSmartHeight();
    }
  }

  protected updated(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ) {
    if (changedProperties.has('value')) {
      this.handleChange();

      if (this.field) {
        this.resizeObserver.disconnect();
        this.resizeObserver.observe(this.field);
      }
    }

    if (changedProperties.has('maxLength')) {
      this.charactersRemaining = this.maxLength;
    }
  }

  disconnectedCallback(): void {
    this.resizeObserver.disconnect();
  }

  render() {
    if (!this.hasLabel) {
      return nothing;
    }

    // Set prettier ignore to not enforce extra white space around the default value text.
    // prettier-ignore
    return html`<div class="${this.getClass()}">
      ${this.labelTemplate()} ${this.helpTextTemplate()}
      <div class="${this.classEl('textarea-wrapper')}">
        <textarea
          class="${this.classEl('textarea')}"
          name="${this.name}"
          id="${this.fieldId || `${this.name}-${this.randomId}-field`}"
          ?required=${this.required}
          ?disabled=${this.disabled}
          ?readonly=${this.readonly}
          resize=${this.resize}
          minlength="${ifDefined(this.minLength)}"
          maxlength="${ifDefined(this.maxLength)}"
          autocomplete="${ifDefined(this.autocomplete)}"
          aria-invalid=${this.errorMessage ? 'true' : 'false'}
          aria-describedby=${this.getTextAreaDescribedBy() || nothing}
          @change=${this.handleChange}
          @input=${this.handleInput}
          @blur=${this.handleBlur}
          @focus=${this.handleFocus}
          value=${ifDefined(this.value)}
        ></textarea>
        <div class="${this.classEl('message-container')}">
          <div>
          ${this.errorMessageTemplate()}
          </div>
          ${this.maxLength !== undefined
            ? html`<span id="pds-textarea-characters-remaining" class="${this.classEl('characters-remaining')}" aria-live="polite">
              ${this.translateText('characters-remaining', {charactersRemaining: this.charactersRemaining})}
              </span>`
            : nothing}
        </div>
      </div>
    </div>`;
  }
}
